import React, { createContext, useState, useContext, useEffect } from 'react';

const TelegramContext = createContext();

// Функция для проверки валидности ID
const isValidUserId = (id) => {
  return id && 
         typeof id === 'string' && 
         id !== "null" && 
         id !== "undefined" && 
         id.trim() !== "";
};

// Функция для безопасного сохранения ID в localStorage
const saveUserId = (id) => {
  if (isValidUserId(id)) {
    localStorage.setItem('user_id', id);
    localStorage.setItem('telegram_user_id', id);
    return true;
  }
  return false;
};

// Функция для полной очистки данных и перезагрузки страницы
const forceUserReset = (oldUserId, newUserId) => {
  console.log(`[TelegramContext] Сброс данных: ${oldUserId} -> ${newUserId}`);
  
  // Очищаем все хранилища
  localStorage.clear();
  sessionStorage.clear();
  
  // Если передан новый ID, сохраняем его
  if (isValidUserId(newUserId)) {
    saveUserId(newUserId);
  }
  
  // Очищаем все куки
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
  }
  
  // Перезагружаем страницу с параметром nocache
  window.location.href = window.location.pathname + 
    (window.location.search ? window.location.search + '&' : '?') + 
    '_nocache=' + Date.now();
};

export const TelegramProvider = ({ children }) => {
  const [telegramUserId, setTelegramUserId] = useState(null);
  const [userPhotoUrl, setUserPhotoUrl] = useState(null);
  const [userName, setUserName] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userLang, setUserLang] = useState('ru');
  const [initializationError, setInitializationError] = useState(null);

  // Функция для безопасной установки ID пользователя
  const setValidUserId = (id) => {
    if (isValidUserId(id)) {
      console.log(`[TelegramContext] Установка ID пользователя: ${id}`);
      setTelegramUserId(id);
      saveUserId(id);
      return true;
    }
    return false;
  };

  // Проверка параметров URL и сохранение Telegram ID
  useEffect(() => {
    const initTelegramUser = async () => {
      try {
        console.log('[TelegramContext] Начало инициализации пользователя');
        
        // 1. Пытаемся получить данные из Telegram WebApp
        if (window.Telegram && window.Telegram.WebApp) {
          const tg = window.Telegram.WebApp;
          tg.expand(); // Расширяем веб-приложение
          
          // Добавляем более подробное логирование
          console.log('[TelegramContext] WebApp доступен:', !!tg);
          console.log('[TelegramContext] initData доступен:', !!tg.initData);
          console.log('[TelegramContext] initDataUnsafe доступен:', !!tg.initDataUnsafe);
          console.log('[TelegramContext] WebApp user доступен:', !!(tg.initDataUnsafe?.user));
          
          // Дамп объекта для отладки
          console.log('[TelegramContext] WebApp объект:', JSON.stringify({
            initDataUnsafe: tg.initDataUnsafe,
            user: tg.initDataUnsafe?.user,
            userAgent: window.navigator.userAgent
          }, null, 2));
          
          // Получаем данные пользователя из инициализационных данных Telegram
          const user = tg.initDataUnsafe?.user;
          
          if (user && user.id) {
            // Важно: преобразуем ID в строку и триммим для надежности
            const userId = String(user.id).trim();
            console.log(`[TelegramContext] Получен ID пользователя из Telegram: ${userId}`);
            
            // Устанавливаем ID пользователя во все хранилища для надежности
            setValidUserId(userId);
            localStorage.setItem('telegram_user_id', userId);
            localStorage.setItem('user_id', userId);
            document.cookie = `telegram_user_id=${userId};path=/;max-age=86400`;
            
            // Устанавливаем данные пользователя
            setUserPhotoUrl(user.photo_url || null);
            setUserName(user.first_name || user.username || 'User');
            setUserLang(user.language_code || 'ru');
            
            // Отправляем данные на сервер для аутентификации
            await sendAuthToServer(user);
            
            setIsInitialized(true);
            return;
          } else {
            console.warn('[TelegramContext] Не удалось получить ID пользователя из WebApp');
          }
        } else {
          console.warn('[TelegramContext] Telegram WebApp не найден в window.Telegram');
        }

        // 2. Проверяем URL-параметры
        const urlParams = new URLSearchParams(window.location.search);
        const urlUserId = urlParams.get('user_id') || urlParams.get('id');
        
        if (isValidUserId(urlUserId)) {
          console.log(`[TelegramContext] Получен ID пользователя из URL: ${urlUserId}`);
          if (setValidUserId(urlUserId)) {
            setIsInitialized(true);
            return;
          }
        }
        
        // 3. Проверяем localStorage
        const storedUserId = localStorage.getItem('user_id') || localStorage.getItem('telegram_user_id');
        
        if (isValidUserId(storedUserId)) {
          console.log(`[TelegramContext] Получен ID пользователя из localStorage: ${storedUserId}`);
          if (setValidUserId(storedUserId)) {
            setIsInitialized(true);
            return;
          }
        }
        
        // 4. Проверяем cookies
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i].trim();
          if (cookie.startsWith('telegram_user_id=') || cookie.startsWith('user_id=')) {
            const cookieValue = cookie.split('=')[1];
            
            if (isValidUserId(cookieValue)) {
              console.log(`[TelegramContext] Получен ID пользователя из cookie: ${cookieValue}`);
              if (setValidUserId(cookieValue)) {
                setIsInitialized(true);
                return;
              }
            }
          }
        }
        
        // Если ID не найден нигде, устанавливаем флаг инициализации
        // но не показываем ошибку - это будет обработано на уровне компонентов
        console.warn('[TelegramContext] ID пользователя не найден, отображаем гостевой интерфейс');
        setIsInitialized(true);
        
      } catch (error) {
        console.error('[TelegramContext] Ошибка инициализации:', error);
        setInitializationError(error.message);
        setIsInitialized(true);
      }
    };
    
    initTelegramUser();
  }, []);

  // Отправка данных аутентификации на сервер
  const sendAuthToServer = async (user) => {
    if (!isValidUserId(user.id)) {
      console.error('[TelegramContext] Попытка отправить невалидный ID:', user.id);
      return;
    }
    
    try {
      const userId = String(user.id).trim();
      console.log(`[TelegramContext] Отправка данных для аутентификации: ID=${userId}`);
      
      // Добавляем дополнительные параметры для более надежной аутентификации
      const response = await fetch("https://fftg.farodinprojects.org:3000/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": userId,
          "X-Telegram-Auth": "true"
        },
        body: JSON.stringify({
          id: userId,
          first_name: user.first_name || "",
          last_name: user.last_name || "",
          username: user.username || "",
          photo_url: user.photo_url || "",
          language: user.language_code || 'ru',
          auth_source: "telegram_webapp"
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('[TelegramContext] Аутентификация успешна:', data);
      } else {
        console.error('[TelegramContext] Ошибка аутентификации:', data);
      }
    } catch (error) {
      console.error('[TelegramContext] Ошибка при отправке данных аутентификации:', error);
    }
  };

  const value = {
    telegramUserId,
    userPhotoUrl,
    userName,
    userLang,
    isInitialized,
    initializationError,
  };

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  );
};

export const useTelegram = () => useContext(TelegramContext);